<template>
  <div class="megamenu-wrapper">
    <div
      class="megamenu row no-gutters justify-center mt-n5 ml-n2 sm:ml-n4"
      :class="megamenuId"
    >
      <MenuParent
        v-for="parent in parents"
        :key="parent.id"
        :data="parent"
        :megamenu-id="megamenuId"
        :opened-menu-id="openedMenuId"
        :open-submenu="openSubmenu"
        :hide-submenu="hideSubmenu"
      />
      <MenuEventMobileButton v-if="false" />
      <MenuChild
        v-for="child in children"
        :key="child.id"
        :data="child"
        :megamenu-id="megamenuId"
        :opened-menu-id="openedMenuId"
        :hide-submenu="hideSubmenu"
      />
    </div>
  </div>
</template>

<script setup>
import { useDebounceFn } from '@vueuse/core'
import { storeToRefs } from 'pinia'
import { computed, defineAsyncComponent, ref, shallowRef, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute } from 'vue-router'

import MenuChild from '@/components/menus/megamenu/TheMegamenuChild.vue'
import MenuParent from '@/components/menus/megamenu/TheMegamenuParent.vue'
import { useCategoriesStore } from '@/stores/CategoriesStore'
import MenuEventMobileButton from '@/components/menus/megamenu/TheMegamenuEventMobileButton.vue'

const { t, locale } = useI18n()
const route = useRoute()

const categoriesStore = useCategoriesStore()
const { getProductCategories, getMixCategories } = storeToRefs(categoriesStore)

const props = defineProps({
  id: {
    type: String,
    default: 'secondary',
    validator(value) {
      return ['secondary', 'homepage', 'modalmenu'].includes(value)
    },
  },
})

const openedMenuId = ref()
const hideSubmenu = ref()
const openSubmenu = ref()

const parents = shallowRef([
  {
    id: 'mix',
    desktopText: t('mix_your_own_goodies'),
    mobileText: t('own_goodies'),
    title: t('mixes_title'),
    link: '',
    svg: defineAsyncComponent(() => import('@/components/svg/birds/BirdMixIt.vue')),
    imageEvent: 'christmas',
    isCurrent: false,
  },
  {
    id: 'ready',
    desktopText: t('show_all_categories'),
    mobileText: t('all_categories'),
    title: t('ready_mixes_title'),
    link: '',
    svg: defineAsyncComponent(() => import('@/components/svg/birds/BirdProducts.vue')),
    imageEvent: 'christmas',
    isCurrent: true,
  },
])

if (locale.value === 'cs' || locale.value === 'sk') {
  parents.value.push({
    id: 'club',
    title: t('subscribers'),
    link: '',
    svg: defineAsyncComponent(() => import('@/components/svg/navbarMenu/NavbarClubSvg.vue')),
    isCurrent: true,
  })
}

// Club packages is not categories, and it has no permalinks.
const children = shallowRef([
  {
    id: 'club',
    items: [
      {
        text: t('gift_of_infinite'),
        description: t('unlimited_long_subscriptions'),
        link: { name: 'clubPath' },
        svg: defineAsyncComponent(() => import('@/components/svg/club/ForeverSvg.vue')),
      },
      {
        text: t('at_three_months'),
        description: t('subscription_for_free_months'),
        link: { name: 'clubPath' },
        image: 'club/3-months',
        svg: defineAsyncComponent(() => import('@/components/svg/club/ThreeMonthsSvg.vue')),
      },
      {
        text: t('at_six_months'),
        description: t('subscription_for_six_months'),
        link: { name: 'clubPath' },
        svg: defineAsyncComponent(() => import('@/components/svg/club/SixMonthsSvg.vue')),
      },
      {
        text: t('at_twelve_months'),
        description: t('subscription_for_twelve_months'),
        link: { name: 'clubPath' },
        svg: defineAsyncComponent(() => import('@/components/svg/club/TwelveMonthsSvg.vue')),
      },
    ],
  },
  {
    id: 'mix',
    items: getMixCategories.value,
  },
  {
    id: 'ready',
    items: getProductCategories.value,
  },
])

const megamenuId = `megamenu--${props.id}`

const currentRoute = computed(() => route.params)

watch(
  () => currentRoute.value,
  () => {
    if (openedMenuId.value) openedMenuId.value = undefined
  },
)

hideSubmenu.value = useDebounceFn((id) => {
  if (openedMenuId.value === id) openedMenuId.value = undefined
}, 500)

openSubmenu.value = useDebounceFn((id) => {
  if (openedMenuId.value !== id) openedMenuId.value = id
}, 150)
</script>

<i18n>
  {
    "cs": {
    "all_categories": "Všechny kategorie",
    "at_six_months": "U šesti měsíčků",
    "at_three_months": "U tří měsíčků",
    "at_twelve_months": "U dvanácti měsíčků",
    "gift_of_infinite": "Dar Nekonečný",
    "i_want_ready_mix": "Chci hotový Mixit",
    "mix_your_own_goodies": "Namíchej si vlastní dobroty",
    "mixes_title": "Mixovat",
    "own_goodies": "Vlastní dobroty",
    "ready_mixes": "Hotové Mixit",
    "ready_mixes_title": "Produkty",
    "show_all_categories": "Zobrazit všechny kategorie",
    "subscribers": "Předplatitelů",
    "subscription_for_free_months": "Předplatné na 3 měsíce",
    "subscription_for_six_months": "Předplatné na 6 měsíců",
    "subscription_for_twelve_months": "Předplatné na 12 měsíců",
    "unlimited_long_subscriptions": "Neomezené dlouhé předplatné"
},
    "sk": {
    "all_categories": "Všetky kategórie",
    "at_six_months": "Pri šiestich mesiačikoch",
    "at_three_months": "Pri troch mesiačikoch",
    "at_twelve_months": "Pri dvanástich mesiačikoch",
    "gift_of_infinite": "Dar Nekonečný",
    "i_want_ready_mix": "Chcem hotový Mixit",
    "mix_your_own_goodies": "Namiešaj si vlastné dobroty",
    "mixes_title": "Mixovať",
    "own_goodies": "Vlastné dobroty",
    "ready_mixes": "Hotové Mixit",
    "ready_mixes_title": "Produkty",
    "show_all_categories": "Zobraziť všetky kategórie",
    "subscribers": "Predplatiteľov",
    "subscription_for_free_months": "Predplatné na 3 mesiace",
    "subscription_for_six_months": "Predplatné na 6 mesiacov",
    "subscription_for_twelve_months": "Predplatné na 12 mesiacov",
    "unlimited_long_subscriptions": "Neobmedzene dlhé predplatné"
},
    "pl": {
    "all_categories": "Wszystkie kategorie",
    "at_six_months": "Sześć miesięcy",
    "at_three_months": "Trzy miesiące",
    "at_twelve_months": "12 miesięcy",
    "gift_of_infinite": "Chrupanie na zawsze",
    "i_want_ready_mix": "Chcę gotowy produkt",
    "mix_your_own_goodies": "Mixuj swoje smakołyki",
    "mixes_title": "Mixuj",
    "own_goodies": "Swoje smakołyki",
    "ready_mixes": "Gotowe produkty",
    "ready_mixes_title": "Produkty",
    "show_all_categories": "Pokaż wszystkie kategorie",
    "subscribers": "Abonentów",
    "subscription_for_free_months": "Subskrypcja na 3 miesiące",
    "subscription_for_six_months": "Subskrypcja na 6 miesięcy",
    "subscription_for_twelve_months": "Subskrypcja na 12 miesięcy",
    "unlimited_long_subscriptions": "Nieograniczona subskrypcja"
},
    "hu": {
    "all_categories": "Kategória megtekintése",
    "at_six_months": "Hat hónaposnál",
    "at_three_months": "Három hónaposnál",
    "at_twelve_months": "A tizenkét hónaposnál",
    "gift_of_infinite": "Végtelen ajándék",
    "i_want_ready_mix": "Kész Mixitet szeretnék",
    "mix_your_own_goodies": "Mixelj saját finomságot",
    "mixes_title": "Mixelni",
    "own_goodies": "Saját finomságot",
    "ready_mixes": "Kész Mixit",
    "ready_mixes_title": "Termékek",
    "show_all_categories": "Minden kategória megtekintése",
    "subscribers": "Előfizetés",
    "subscription_for_free_months": "Előfizetés három hónapra",
    "subscription_for_six_months": "Elófizetés hat hónapra",
    "subscription_for_twelve_months": "Előfizetés 12 hónapra",
    "unlimited_long_subscriptions": "Korlátlan idejű előfizetés"
},
    "de": {
    "all_categories": "Alle Kategorien",
    "at_six_months": "6 Monate",
    "at_three_months": "3 Monate",
    "at_twelve_months": "12 Monate",
    "gift_of_infinite": "Das unendliche Geschenk",
    "i_want_ready_mix": "Ich will ein fertiges Mixit",
    "mix_your_own_goodies": "Mix dir deine eigenen Leckereien",
    "mixes_title": "Mixen",
    "own_goodies": "Eigene Leckereien",
    "ready_mixes": "Fertig Mixit",
    "ready_mixes_title": "Produkte",
    "show_all_categories": "Alle Kategorien anzeigen",
    "subscribers": "Abonnenten",
    "subscription_for_free_months": "Abonnement für 3 Monate",
    "subscription_for_six_months": "Abonnement für 6 Monate",
    "subscription_for_twelve_months": "Abonnement für 12 Monate",
    "unlimited_long_subscriptions": "Unbegrenztes Langzeitabonnement"
},
    "at": {
    "all_categories": "Alle Kategorien",
    "at_six_months": "6 Monate",
    "at_three_months": "3 Monate",
    "at_twelve_months": "12 Monate",
    "gift_of_infinite": "Das unendliche Geschenk",
    "i_want_ready_mix": "Ich will ein fertiges Mixit",
    "mix_your_own_goodies": "Mix dir deine eigenen Leckereien",
    "mixes_title": "Mixen",
    "own_goodies": "Eigene Leckereien",
    "ready_mixes": "Fertig Mixit",
    "ready_mixes_title": "Produkte",
    "show_all_categories": "Alle Kategorien anzeigen",
    "subscribers": "Abonnenten",
    "subscription_for_free_months": "Abonnement für 3 Monate",
    "subscription_for_six_months": "Abonnement für 6 Monate",
    "subscription_for_twelve_months": "Abonnement für 12 Monate",
    "unlimited_long_subscriptions": "Unbegrenztes Langzeitabonnement"
},
    "ro": {
    "all_categories": "Toate categoriile",
    "at_six_months": "La abonamentul pe șase luni",
    "at_three_months": "La abonamentul pe trei luni",
    "at_twelve_months": "La abonamentul pe 12 luni",
    "gift_of_infinite": "Dar nesfârșit",
    "i_want_ready_mix": "Mixit gata preparat",
    "mix_your_own_goodies": "Amestecă propriile bunătăți",
    "mixes_title": "Mixează",
    "own_goodies": "Propriile bunătăți",
    "ready_mixes": "Mixit gata preparat",
    "ready_mixes_title": "Produse",
    "show_all_categories": "Vizualizează toate categoriile",
    "subscribers": "Abonaților",
    "subscription_for_free_months": "Abonament pe 3 luni",
    "subscription_for_six_months": "Abonament pe șase luni",
    "subscription_for_twelve_months": "Abonament pe 12 luni",
    "unlimited_long_subscriptions": "Abonamente nelimitate"
}
  }
</i18n>
